/**
 * @fileoverview Export mutation hook for Sales Performance.
 * @description Requests the CSV for the selected period and team. It changes nothing in the
 * Sales data (the backend only audits the export), so no query is refreshed; the screen saves the
 * returned file and shows a refusal inline.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation } from '@tanstack/react-query'

import { exportSalesPerformance } from '@/features/sales/services/sales-performance.service'

import type { SalesPerformanceQuery } from '@/features/sales/types/sales-performance.types'

/**
 * @description Exports the selected period and team.
 * @param {(file: { filename: string; csv: string }, query: SalesPerformanceQuery) => void} [onSuccess]
 * - Called with the file and the selection it was exported for, after a success, even if the page
 * unmounted since.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the period and team).
 */
export function useExportSalesPerformance(
  onSuccess?: (file: { filename: string; csv: string }, query: SalesPerformanceQuery) => void,
) {
  return useMutation({
    mutationFn: (query: SalesPerformanceQuery) => exportSalesPerformance(query),
    /**
     * @description Hands the exported file on.
     * @param {{ filename: string; csv: string }} file - The exported file.
     * @param {SalesPerformanceQuery} query - The selection it was exported for.
     */
    onSuccess: (file, query) => {
      onSuccess?.(file, query)
    },
  })
}
